import { Button as NextButton } from '@nextui-org/react';
import { Popover } from '@nextui-org/react';
import styled, { css } from 'styled-components';

import { ChevronDownIcon, GearIcon, OrganizationIcon, PlusIcon, SearchIcon } from '../../../assets';
import KebabMenuIcon from '../../../assets/KebabMenuIcon';
import { Button, Dropdown, IconButton, ShimmerText } from '../../../components';
import MobileCreateButton from '../../../components/Button/MobileCreateButton';
import InputErrorWrapper from '../../../components/Input';
import AppSearchInput from '../../../components/Input/SearchInput';
import { twoLevelHeaderHeight } from '../../../components/layout/Header/Header.styles';
import ZIndexLayers from '../../../services/ZIndexLayers';
import { ThemeProp } from '../../../theme/Themes';
import { DESKTOP_RIGHT_PADDING, HeaderIconButtonProps, ListingsIconButton } from '../Listing/Listing.styles';
import { HorizontalCarousel } from './components';

const sidesPadding = 40 / 16;
const sidesPaddingMobile = 1;

export const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    margin-top: ${twoLevelHeaderHeight};
  }
`;

export const DesktopHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 ${sidesPadding}rem 1rem ${sidesPadding}rem;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    padding: 0 ${sidesPaddingMobile}rem 1rem ${sidesPaddingMobile}rem;
  }

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

export const MobileHeader = styled.div`
  display: flex;
  flex-direction: row;
  background-color: ${(props: ThemeProp) => props.theme.colors.white};
  align-items: center;
  padding: 0.5rem;
  display: none;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: inline-flex;
    position: fixed;
    z-index: ${ZIndexLayers.overlay};
    top: 0;
    width: 100%;
  }
`;

export const SearchContainer = styled.div`
  display: flex;
  width: 15.6rem;
  padding-right: 2rem;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.desktop}) {
    padding-right: 1rem;
  }
`;

export const StatusFilterContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-right: auto;
  gap: 0.75rem;
  overflow-x: scroll;
  align-items: center;
  ::-webkit-scrollbar {
    display: none;
  }
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    width: 100%;
  }
`;

export const SortComponent = styled.div`
  background-color: yellow;
  height: 2rem;
  min-width: 10rem;
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    min-width: 0;
    width: 6.5rem;
    align-self: flex-end;
  }
`;

export const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  padding-left: ${sidesPadding}rem;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    padding-left: 0;
  }
`;

interface FiltersContainerProps {
  $maxHeight?: string;
}

export const FiltersContainer = styled.div<FiltersContainerProps>`
  width: 14.0625rem;
  display: flex;
  flex-direction: column;
  top: auto;
  bottom: 0;
  z-index: ${ZIndexLayers.elevateOne};
  background: ${(props: ThemeProp) => props.theme.colors.layout.surface.default};
  overflow-y: auto;
  max-height: ${(props: FiltersContainerProps) => props.$maxHeight || '32rem'};

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

export const FiltersListMobileContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${ZIndexLayers.fullScreenOverlay};
  background-color: ${(props: ThemeProp) => props.theme.colors.primary.c900};
  overflow-y: scroll;
  display: none;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    display: flex;
  }
`;

export const DummySearchComponent = styled.div`
  display: flex;
  flex: 1;
  background-color: yellow;
  height: 2rem;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    max-width: 9.75rem;
    margin-right: auto;
  }
`;

export const DummySortComponent = styled.div`
  background-color: yellow;
  height: 2rem;
  width: 10rem;
`;

export const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: visible;
`;

export const ListFrame = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 1rem;
  overflow: visible;
  width: 100%;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    margin-top: 0;
    gap: 0.75rem;
  }
`;

export const ListTopView = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  gap: 0.5rem;

  @media (min-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    padding-right: ${DESKTOP_RIGHT_PADDING};
  }

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    padding: 0 1rem;
  }

  @media (min-width: ${(props: ThemeProp) => props.theme.breakpoints.minMobile}) {
    min-height: 2.5rem;
  }
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    width: 100%;
    padding-top: 0.75rem;
  }
`;

export const ListTopSideView = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const ListTopCenterView = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  overflow: auto;
  position: relative;

  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

export const ListingTitle = styled.div`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.l};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.xl};
  text-overflow: ellipsis;
  overflow: hidden;
  display: flex;
  align-items: center;
`;

export const StyledSearchIcon = styled(SearchIcon)`
  stroke: ${(props: ThemeProp) => props.theme.colors.black};
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    stroke: ${(props: ThemeProp) => props.theme.colors.greyscale.c600};
  }
`;

export const SearchInput = styled(InputErrorWrapper).attrs((props) => ({
  ...props,
  css: {
    [`@media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet})`]: {
      display: 'none',
    },
    '&.nextui-input-main-container': {
      marginBottom: 0,
      height: '2.5rem',
      [`@media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile})`]: {
        height: '2rem',
      },
    },
    ...props.css,
  },
}))`
  && {
    border-color: ${(props: ThemeProp) => props.theme.colors.greyscale.c300};
    margin: 0;
    min-width: 13.5rem;
  }
`;

export const ImportButton = styled(Button)`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.s};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  color: ${(props: ThemeProp) => props.theme.colors.black};
  gap: unset;

  border-right: 1px solid ${(props: ThemeProp) => props.theme.colors.primary.c200};

  .nextui-button-text {
    z-index: ${ZIndexLayers.default};
  }

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const CreateVehicleTabletButton = styled(Button)`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.s};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  color: ${(props: ThemeProp) => props.theme.colors.white};
  gap: unset;
`;

export const DropDownButton = styled(NextButton).attrs({ rounded: true })`
  position: relative;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.s};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c800};
  gap: unset;
  min-width: 2.5rem;
  z-index: ${ZIndexLayers.default};

  &:not(:disabled) {
    background: #d501f1; // NOTE: Custom color from design specs.
  }

  &:hover {
    background: ${({ theme }: ThemeProp) => theme.colors.primary.c500};
  }

  &:focus {
    background: ${({ theme }: ThemeProp) => theme.colors.primary.c600};
  }

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const CreateVehicleButton = styled(Button)`
  background: transparent;
  justify-content: start;
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
  &:hover {
    background: transparent;
  }
`;

export const PopoverContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: ${({ theme }: ThemeProp) => theme.colors.secondary['12-light-blue']};
`;

export const StatusesDropdown = styled(Dropdown).attrs(
  (props: ThemeProp & { isDisabled: boolean; noPaddingLeft?: boolean }) => ({
    buttonCSS: {
      '&.nextui-dropdown-button': {
        background: props.isDisabled ? props.theme.colors.greyscale.c050 : 'transparent',
        color: props.isDisabled ? props.theme.colors.greyscale.c600 : 'transparent',
        border: 'none',
        borderRadius: '6px',
        paddingLeft: props.noPaddingLeft ? '0' : '0.75rem',
      },
      '&.nextui-dropdown-button[aria-haspopup="true"].nextui-dropdown-button[aria-expanded="true"]': {
        transform: 'unset',
        border: 'unset',
      },
      '&.nextui-dropdown-button .nextui-button-text': {
        marginRight: '0.25rem',
      },
      '&.nextui-dropdown-button:focus': {
        background: 'transparent',
        border: 'unset',
        color: props.theme.colors.greyscale.c050,
        boxShadow: 'none',
      },
      '& .nextui-button-icon-right': {
        background: 'transparent',
        height: '1.5rem',
        marginLeft: '0px',
        marginTop: '0.25rem',
        width: '1.5rem',
      },
      '&.nextui-dropdown-button:hover': {
        background: props.theme.colors.primary.c800,
      },
    },
    ...props,
  }),
)`
  .nextui-button-text {
    font-size: ${({ theme }: ThemeProp) => theme.fontVariants.desktop.h2.fontSize};
    font-weight: ${({ theme }: ThemeProp) => theme.fontWeight.medium};
    line-height: ${(props: ThemeProp) => props.theme.lineHeight.lxl};
    margin-right: 0.5rem;
    text-transform: capitalize;

    @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
      font-size: ${(props: ThemeProp) => props.theme.fontSize.mlg};
      line-height: ${(props: ThemeProp) => props.theme.lineHeight.m};
      font-weight: ${({ theme }: ThemeProp) => theme.fontWeight.medium};
      margin-right: 0.25rem;
    }
  }
  svg {
    width: 0.78rem;
    height: 0.78rem;
    path {
      stroke-width: 1.8px;
    }
  }

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    svg {
      width: 0.7rem;
      height: 0.7rem;
      path {
        stroke-width: 1.5px;
      }
    }
  }
`;

export const ButtonGroup = styled(NextButton.Group)`
  margin: 0;
`;

export const FilterButtonContainer = styled.div`
  border: none;
  margin-left: 0.625rem;
  display: none;
  position: relative;
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    display: flex;
  }
`;

export const FilterButton = styled.div`
  border-radius: ${(props: ThemeProp) => props.theme.borderRadius};
  border-color: ${(props: ThemeProp) => props.theme.colors.greyscale.c200};
  background-color: ${(props: ThemeProp) => props.theme.colors.white};
  stroke: ${(props: ThemeProp) => props.theme.colors.white};
  :focus {
    box-shadow: unset;
  }
  display: flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;

  background-color: transparent;
  z-index: ${ZIndexLayers.default};

  &:hover {
    background: ${(props: ThemeProp) => props.theme.colors.greyscale.c100};
  }
`;

export const HorizontalDivider = styled.div`
  width: 1px;
  height: 2rem;
  background: ${(props: ThemeProp) => props.theme.colors.greyscale.c300};
`;

export const StyledChevronDownIcon = styled(ChevronDownIcon)`
  stroke: ${(props) => props.theme.colors.black};
`;

export const StyledPopoverContent = styled(Popover.Content)`
  background: ${(props: ThemeProp) => props.theme.colors.layout.surface.default};
  overflow: hidden;
  border-radius: 0.5rem;
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
  box-shadow: ${(props: ThemeProp) => props.theme.shadows.menu};
  margin-right: 2px;
`;

const displayOnMobilePartial = css`
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: flex;
  }
`;

const displayOnTabletAndBelowPartial = css`
  display: none;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    display: flex;
  }
`;

// TODO: we must address the issue with the mobile breakpoint not being a simple px measure.
const displayOnTabletPartial = css`
  display: none;

  // min-width: 480px (mobile breakpoint) + 1, because if we do not add 1, we will show it in 480px and we should not.
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) and (min-width: 481px) {
    display: flex;
  }
`;

const hideOnMobilePartial = css`
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: none;
    margin-right: 0;
  }
`;

const aboveMobilePartial = css`
  @media (min-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

const aboveTabletPartial = css`
  display: none;

  @media (min-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    display: flex;
  }
`;

interface ActionsRowItemProps {
  $mobileOnly?: boolean;
  $tabletOnly?: boolean;
  $tabletAndBelow?: boolean;
  $hideMobile?: boolean;
  $aboveMobileOnly?: boolean;
  $aboveTabletOnly?: boolean;
  $isShimmer?: boolean;
  $mobileFlex?: number;
  $width?: string;
}

export const ActionsRowItem = styled.div<ActionsRowItemProps>`
  display: ${({ $mobileOnly }: ActionsRowItemProps) => ($mobileOnly ? 'none' : null)};
  ${({ $mobileOnly }: ActionsRowItemProps) => ($mobileOnly ? displayOnMobilePartial : null)};
  ${({ $tabletOnly }: ActionsRowItemProps) => ($tabletOnly ? displayOnTabletPartial : null)};
  ${({ $tabletAndBelow }: ActionsRowItemProps) => ($tabletAndBelow ? displayOnTabletAndBelowPartial : null)};
  ${({ $hideMobile }: ActionsRowItemProps) => ($hideMobile ? hideOnMobilePartial : null)};
  ${({ $aboveMobileOnly }: ActionsRowItemProps) => ($aboveMobileOnly ? aboveMobilePartial : null)};
  ${({ $aboveTabletOnly }: ActionsRowItemProps) => ($aboveTabletOnly ? aboveTabletPartial : null)};

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    ${(props: ActionsRowItemProps) => props.$width && `width: ${props.$width};`}
    ${(props: ActionsRowItemProps) => props.$mobileFlex && `flex: ${props.$mobileFlex};`};

    &:nth-last-of-type(2) {
      ${({ $isShimmer }: ActionsRowItemProps) => !$isShimmer && 'margin-right: 0 !important; margin-left: 0.5rem;'};
    }
  }
`;

export const ActionsRow = styled.div`
  display: flex;
  align-items: center;

  ${ActionsRowItem} {
    &:not(:last-of-type) {
      margin-right: 0.5rem;
    }
  }

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    width: 100%;

    ${ActionsRowItem} {
      &:not(:last-of-type) {
        margin-right: 0rem;
      }
    }
  }
`;

export const HeaderIconButton = styled(ListingsIconButton)`
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    background: transparent;
    box-shadow: unset;

    &:hover {
      background: transparent;
    }
  }
`;

export const StyledKebabMenuIcon = styled(KebabMenuIcon)`
  transform: rotate(90deg);
`;

export const SearchButton = styled.button<SearchButtonProps>`
  margin: 0;
  padding: 0;
  font-size: 0;
  line-height: 0;
  width: 2.5rem;
  height: 2.5rem;
  cursor: pointer;
  border: 0;
  border-radius: 50%;
  background-color: ${(props: ThemeProp & SearchButtonProps) =>
    props.$focused ? 'transparent' : props.theme.colors.secondary['12-light-blue']};

  svg {
    stroke: ${(props: ThemeProp & SearchButtonProps) => props.theme.colors.primary.c050};
  }

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    background-color: transparent;
  }
`;

export const StyledSearchInput = styled(AppSearchInput).attrs((props) => {
  return {
    ...props,
    desktopWidth: '14.375rem',
  };
})``;

interface SearchButtonProps {
  $focused?: boolean;
}

export const StyledIconButton = styled(IconButton)``;

export const Triangle = styled.div`
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid ${(props: ThemeProp) => props.theme.colors.secondary['12-light-blue']};
  position: absolute;
  top: -8px;
  right: 24px;
  display: flex;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.large}) {
    display: none;
  }
`;

export const NotificationsStyledGearIcon = styled(GearIcon)`
  stroke: ${(props: ThemeProp) => props.theme.colors.white};
`;

export const ConfigurationButton = styled(HeaderIconButton)`
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    background: ${(props: ThemeProp & HeaderIconButtonProps) =>
      props.$focused ? props.theme.colors.primary.c800 : props.theme.colors.greyscale.c900};
    border: none;
    border: 1px solid ${(props) => props.theme.colors.greyscale.c800};
  }
`;

export const StyledOrganizationIcon = styled(OrganizationIcon)`
  width: 1rem;
  height: 1rem;
  stroke: ${(props) => props.theme.colors.white};
`;

export const StyledMobileCreateButton = styled(MobileCreateButton)`
  display: none;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: flex;
  }
`;

export const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const StyledFilterPlusIcon = styled(PlusIcon)`
  path {
    stroke: ${(props: ThemeProp) => props.theme.colors.white};
  }
`;

export const StyledAddVehiclePlusIcon = styled(PlusIcon)`
  path {
    stroke: ${(props: ThemeProp) => props.theme.colors.black};
  }
`;

export const DesktopShimmerAddVehicleButton = styled(ShimmerText)`
  display: flex;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

export const MobileTabletShimmerAddVehicleButton = styled(ShimmerText)`
  display: none;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: flex;
  }
`;

export const AddFilterButton = styled(Button).attrs((props: any) => {
  return {
    ...props,
    css: {
      ...props.buttonCSS,
      color: props.theme.colors.white,
      borderRadius: '0.5rem',
      minHeight: '2.25rem',
      maxHeight: props.maxHeight ? props.maxHeight : 'unset',
      'border-radius': '$xs',
      'border-color': props.theme.colors.greyscale.c400,
      fontSize: props.fontSize || '$input',
      fontWeight: props.fontWeight || '$normal',
      background: props.theme.colors.layout.surface.default,
      minWidth: '5.75rem !important',
      '.nextui-button-text': {
        fontSize: props.theme.fontSize.s,
        color: props.theme.colors.content.default,
      },
      border: `1px solid ${props.$isOpen ? props.theme.colors.main.c600 : props.theme.colors.neutrals.c200}`,
      '&:focus': {
        background: props.theme.gradients.button.black,
      },
      '&:hover': {
        background: !props.$isOpen ? props.theme.colors.neutrals.c050 : null,
        border: !props.$isOpen ? `1px solid ${props.theme.colors.neutrals.c200}` : null,
      },
      '&:disabled': {
        background: props.theme.colors.greyscale.black,
        color: props.theme.colors.greyscale.c200,
      },
      boxShadow: 'none !important',
    },
  };
})<any>`
  & .nextui-button-text {
    div {
      gap: 0.25rem;
    }
    svg {
      path {
        stroke: ${(props: ThemeProp) => props.theme.colors.neutrals.c500};
      }
    }
  }

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    height: 2.25rem;
  }
`;

export const MobileAddFilterButtonWrapper = styled.div`
  position: relative;
`;

const COUNT_COMPONENT_SIZE = '0.875rem';

export const CountComponent = styled.div`
  position: absolute;
  right: -0.3125rem;
  top: -0.3125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${COUNT_COMPONENT_SIZE};
  height: ${COUNT_COMPONENT_SIZE};
  line-height: ${COUNT_COMPONENT_SIZE};
  padding-left: 0;
  font-size: 0.563rem; // NOTE: Design Specs
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
  color: ${(props: ThemeProp) => props.theme.colors.layout.surface.default};
  background: ${(props: ThemeProp) => props.theme.colors.main.c600};
  border-radius: 50%;
  z-index: ${ZIndexLayers.overlay};
`;

export const StyledNextPopover = styled(Popover)`
  margin-right: 2px;
`;

export const StyledHorizontalCarousel = styled(HorizontalCarousel).attrs(() => ({
  gradient: 'linear-gradient(270deg, #fbf9ff 25%, rgba(251, 249, 255, 0) 100%)', // NOTE: Spec from design system.
}))``;

export const MobileSharedFilterContainer = styled.div`
  padding: 0.25rem 1rem;
`;
